import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ApiError } from "@core/http";
import { useSession } from "@core/session/SessionProvider";
import { consumeReturnPath } from "@portal/shell/IdleSessionGuard";
import { signIn } from "./api";
import { AccessLayout } from "./AccessLayout";
import {
  BigButton,
  Field,
  LinkButton,
  Notice,
  PasswordField,
} from "./primitives";

// SPEC-221 §2 y §6: la pantalla de ingreso del portal. Dos campos, sin
// selector de perfil (lo determina el usuario ingresado). Los textos de
// error reutilizan literal el catálogo de SPEC-003 §6.
//
// Sin checkbox "Mantener la sesión iniciada": ese campo del prototipo
// (validador.jsx) es RF-VAL-03, un requisito del validador de comercio
// ("uso recurrente en mostrador"), sin RF equivalente para el portal — se
// copió por error al adaptar el prototipo en la corrida original de
// SPEC-221 y se retiró acá. `keepSession` tampoco lo usa el backend
// (`api/src/core/access/staff-access/staff-access.rules.ts`, `signInPortal`
// ignora ese campo del body).
const INVALID_CREDENTIALS_MESSAGE =
  "Usuario o contraseña incorrectos. Tras cinco intentos la cuenta se bloquea.";

const ACCOUNT_LOCKED_MESSAGE =
  "Cuenta bloqueada temporalmente. Vuelve a intentarlo más tarde.";

function accountLockedMessage(retryAfterSeconds: number | undefined): string {
  if (retryAfterSeconds === undefined) return ACCOUNT_LOCKED_MESSAGE;
  const minutes = Math.ceil(retryAfterSeconds / 60);
  return `Cuenta bloqueada temporalmente. Intenta en ${String(minutes)} minutos.`;
}

const NETWORK_ERROR_MESSAGE =
  "No se pudo conectar con el servidor. Intenta de nuevo.";

export function LoginScreen() {
  const navigate = useNavigate();
  const { applySession } = useSession();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [locked, setLocked] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const disabled = locked || submitting;

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (disabled) return;
    setError(null);
    setSubmitting(true);
    try {
      const session = await signIn(username, password);
      applySession(session);
      const returnPath = consumeReturnPath();
      navigate(returnPath ?? "/", { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 401) {
          setError(INVALID_CREDENTIALS_MESSAGE);
        } else if (err.status === 429) {
          setLocked(true);
          setError(accountLockedMessage(err.problem?.retryAfterSeconds));
        } else {
          setError(NETWORK_ERROR_MESSAGE);
        }
      } else {
        setError(NETWORK_ERROR_MESSAGE);
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AccessLayout sub="Portal administrativo" title="Acceso al portal">
      <form onSubmit={(event) => void handleSubmit(event)}>
        <fieldset
          disabled={disabled}
          style={{ border: "none", padding: 0, margin: 0 }}
        >
          {error ? <Notice>{error}</Notice> : null}
          <Field
            label="Usuario"
            value={username}
            onChange={setUsername}
            placeholder="usuario"
            autoComplete="username"
            error={Boolean(error)}
          />
          <PasswordField
            label="Contraseña"
            value={password}
            onChange={setPassword}
            placeholder="••••••••"
            autoComplete="current-password"
            error={Boolean(error)}
          />
          <BigButton type="submit">
            {submitting ? "Ingresando…" : "Ingresar"}
          </BigButton>
        </fieldset>
        <div style={{ textAlign: "center", marginTop: 6 }}>
          <LinkButton
            onClick={() => {
              navigate("/access/recover");
            }}
          >
            ¿Olvidaste tu contraseña?
          </LinkButton>
        </div>
      </form>
    </AccessLayout>
  );
}
